import { CatalogHelpers } from "@/tools/CatalogHelpers";
import { OMath } from "@/tools/OMath";

const orderKey = "opentable-order";

const GIFT_TYPE = {
  // ===== Valores adicioniais =====
  ADDITION: 1,

  // ===== Forma de cobrança =====
  PRICE: 2,
  PERCENT: 4,

  // ===== Cobrar com base em =====
  CART: 8,
  DELIVERY: 16,

  // ===== Tipo de uso do cupom =====
  FIRST_PURCHASE_EVER: 32,
  N_USE: 64,
  FREE_TO_USE: 128,
  NEXT_PURCHARSE: 256,
  CASHBACK_USE: 512,
  CASHBACK_EARN: 1024,
};

export const OrderEvents = {
  ORDER_UPDATED: "orderUpdated",
};
export class OrderStorage {
  static initOrder() {
    const orderTemplate = {
      customer: {},
      cart: {
        catalog: {
          service: null,
          items: [],
          type: "catalog",
          quantity: 1,
          selected: true,
        },
        total: 0,
      },
      payment: { payer: null, method: null },
      delivery: { type: null },
      gift: null,
      total: 0,
    };

    const unparsedData = JSON.stringify(orderTemplate);
    localStorage.setItem(orderKey, unparsedData);
    return orderTemplate;
  }

  static addItemToCart(cartItem) {
    const order = OrderStorage.getOrder();
    order.cart.catalog.items.push(cartItem);
    order.cart.total = CatalogHelpers.calculateSelectedItemPrice(
      order.cart.catalog
    );
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static updateCartItem(cartItemIndex, cartItem) {
    const order = OrderStorage.getOrder();
    order.cart.catalog.items[cartItemIndex] = cartItem;
    order.cart.total = CatalogHelpers.calculateSelectedItemPrice(
      order.cart.catalog
    );
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static removerCartItem(cartItemIndex) {
    const order = OrderStorage.getOrder();
    order.cart.catalog.items = order.cart.catalog.items.filter(
      (_, cIndex) => cIndex != cartItemIndex
    );
    order.cart.total = CatalogHelpers.calculateSelectedItemPrice(
      order.cart.catalog
    );
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static updateCartCatalogService(service) {
    const order = OrderStorage.getOrder();
    order.cart.catalog.service = service;
    OrderStorage.setOrder(order);
  }

  static clearCartItems() {
    const order = OrderStorage.getOrder();
    order.cart.catalog.items = [];
    order.cart.total = 0;
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static getCustomer() {
    const order = OrderStorage.getOrder();
    return order.customer || {};
  }

  static setCustomer(customer) {
    const order = OrderStorage.getOrder();
    order.customer = customer;
    OrderStorage.setOrder(order);
  }

  static getDelivery() {
    const order = OrderStorage.getOrder();
    return order.delivery;
  }

  static setDelivery(delivery) {
    const order = OrderStorage.getOrder();
    order.delivery = delivery;
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static resetDelivery() {
    const order = OrderStorage.getOrder();
    order.delivery = { type: 1, address: null, total: null };
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static setDeliveryType(type) {
    const order = OrderStorage.getOrder();
    order.delivery.type = type;
    OrderStorage.setOrder(order);
  }

  static setPagerCode(code) {
    const order = OrderStorage.getOrder();
    order.pagerCode = code;
    OrderStorage.setOrder(order);
  }

  static setPaymentPayer(payer) {
    const order = OrderStorage.getOrder();
    order.payment.payer = payer;
    OrderStorage.setOrder(order);
  }

  static setPaymentMethod(method) {
    const order = OrderStorage.getOrder();
    order.payment.method = method;
    OrderStorage.setOrder(order);
  }

  static setDeliveryAddress(address) {
    const order = OrderStorage.getOrder();
    order.delivery.address = address;
    OrderStorage.setOrder(order);
  }

  static setTotal(total) {
    const order = OrderStorage.getOrder();
    order.total = total;
    OrderStorage.setOrder(order);
  }

  static getOrder() {
    try {
      const orderRaw = localStorage.getItem(orderKey);
      if (!orderRaw) {
        return OrderStorage.initOrder();
      }

      return JSON.parse(orderRaw);
    } catch (err) {
      return null;
    }
  }

  static setOrder(data) {
    const unparsedData = JSON.stringify(data);

    localStorage.setItem(orderKey, unparsedData);
    if (window) {
      window.dispatchEvent(
        new CustomEvent(OrderEvents.ORDER_UPDATED, { detail: data })
      );
    }
  }

  static setFlow(type) {
    const order = OrderStorage.getOrder();
    order.flow = type;
    OrderStorage.setOrder(order);
  }

  static get flow() {
    const order = OrderStorage.getOrder();
    return order.flow;
  }

  static typeIs(gift, type) {
    return (gift.type & type) == type;
  }

  static getGift() {
    const order = OrderStorage.getOrder();
    return order.gift;
  }

  static setGift(gift) {
    const order = OrderStorage.getOrder();
    order.gift = gift;
    OrderStorage.setOrder(OrderStorage.adjustTotal(order));
  }

  static getGiftValue(gift, cartValue, deliveryValue = 0) {
    let baseValue = 0;

    if (OrderStorage.typeIs(gift, GIFT_TYPE.DELIVERY)) {
      baseValue = deliveryValue;
    } else if (OrderStorage.typeIs(gift, GIFT_TYPE.CART)) {
      baseValue = cartValue;
    }

    const v = gift.value || 0;
    let finalGiftValue = baseValue;

    if (OrderStorage.typeIs(gift, GIFT_TYPE.PERCENT)) {
      const percentage = OMath.times(Math.min(v, 100), 0.01);
      finalGiftValue = OMath.times(baseValue, percentage);
    } else if (OrderStorage.typeIs(gift, GIFT_TYPE.PRICE)) {
      finalGiftValue = !OrderStorage.typeIs(gift, GIFT_TYPE.ADDITION)
        ? Math.min(baseValue, v)
        : v;
    }

    const signal = OrderStorage.typeIs(gift, GIFT_TYPE.ADDITION) ? 1 : -1;
    return +OMath.times(Math.abs(finalGiftValue), signal).toFixed(2);
  }

  static adjustTotal(order) {
    const { gift } = order;

    const has_gift = !!gift;
    const is_cashback_earn =
      has_gift && OrderStorage.typeIs(gift, GIFT_TYPE.CASHBACK_EARN);

    if (has_gift) {
      if (OrderStorage.typeIs(gift, GIFT_TYPE.DELIVERY)) {
        gift.value = order.delivery?.total;
      }

      gift.total = OrderStorage.getGiftValue(
        gift,
        order.cart.total,
        order.delivery?.total
      );

      const order_subtotal = +OMath.sum(
        order.cart.total || 0,
        order.delivery?.total || 0
      );

      if (!is_cashback_earn && Math.abs(gift.total) >= order_subtotal - 1) {
        gift.total = -(order_subtotal - 1);
      }

      order.gift = gift;
    }

    order.total = +OMath.sum(
      order.cart.total || 0,
      order.delivery?.total || 0,
      is_cashback_earn ? 0 : order.gift?.total || 0
    );

    return order;
  }
}
